import { pushAction, inProgress } from '../../logic/undoHistory.ts';
import { liveAppData } from '../../store/store.ts';

type Constructor<T = HTMLElement> = new (...args: any[]) => T;
type AbstractConstructor<T = HTMLElement> = abstract new (...args: any[]) => T;

export function HistoryTrackingMixin<TBase extends AbstractConstructor>(Base: TBase) {
  abstract class WithHistoryTracking extends Base {
    /** Signals to as-configurator that this element self-manages history. */
    readonly _historyManaged = true;

    protected abstract _getHistoryValue(): any;

    /** Override to return true when pushing history should also clear the redo tail. */
    protected _historyEntryClearsRedo(): boolean { return false; }

    /** Set true before calling setValue/internal-set to skip the next history push. Always cleared after. */
    protected _skipNextHistoryPush = false;

    private readonly _handleOwnInputChange = (_e: Event): void => {
      const skip = this._skipNextHistoryPush;
      this._skipNextHistoryPush = false;
      if (skip) return;
      if (inProgress()) return;
      if (this.hasAttribute('ignore-history')) return;
      if (this.hasAttribute('trcad-param') && this.hasAttribute('configure-on-change')) return;
      if (!this.id) return;

      const valueBefore = liveAppData.uiInputs[this.id] ?? null;
      const valueAfter = this._getHistoryValue();
      if (valueAfter === valueBefore) return;

      pushAction({
        type: 'input',
        inputId: this.id,
        valueBefore,
        valueAfter,
        ...(this._historyEntryClearsRedo() ? { clearsRedoOnUndo: true } : {}),
      });
    };

    protected _registerHistoryTracking(): void {
      this.addEventListener('as-input-change', this._handleOwnInputChange);
    }

    protected _unregisterHistoryTracking(): void {
      this.removeEventListener('as-input-change', this._handleOwnInputChange);
    }
  }
  return WithHistoryTracking;
}
